import React, { useEffect, useMemo, useState } from "react";
import "./questions.scss";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import QuestionTable from "./QuestionTable";
import BreadCrumb from "../../components/Breadcrumb/BreadCrumb";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteQuestion,
  getQuestionList,
  addQuestion,
  editQuestion,
  getAssociateQuestions,
  associateQuestions,
  getTopicViaId,
  clearQuestionList,
  disassociateQuestion,
  addBulkQuestions,
} from "../../store/questions";
import DeleteModal from "../DeleteModal/DeleteModal";
import QuestionModal from "../QuestionModal/QuestionModal";
import QuestionUploadModal from "../QuestionUploadModal/QuestionUploadModal";
import NoDataAvailable from "../NoDataAvailable/NoDataAvailable";
import AssociateQuestions from "./AssociateQuestions";
import Constants from "../../constants/constants";
import Loader from "../Loader/Loader";
import {
  getBootcampById,
  getBootcampModuleView,
} from "../../store/bootcampModuleView";
import { getDetails } from "../../store/SessionView/detailsTab";
import { checkDateTimeStatus, encode } from "../../utils/utils";
import { useNavigate } from "react-router-dom/dist";
import dayjs from "dayjs";
import DisassociateQuestionModal from "./DisassociateQuestionModal";
import BootstrapTooltip from "../BootstrapTooltip/BootstrapTooltip";

// Component for Associate Questions Button
const AssociateQuestionsButton = ({
  viaSession,
  isAccessible,
  moduleIsConcluded,
  handleAssociateQuestionsModalOpen,
}) => {
  if (!viaSession || !isAccessible) return null;

  return (
    <BootstrapTooltip title={moduleIsConcluded ? "Module is completed" : null}>
      <Box>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={handleAssociateQuestionsModalOpen}
          disabled={moduleIsConcluded}
        >
          <Typography variant="outlineBtnLabel">associate questions</Typography>
        </Button>
      </Box>
    </BootstrapTooltip>
  );
};

// Component for Add Question and Upload Questions Buttons
const QuestionActionButtons = ({
  viaTopic,
  viaSession,
  isAccessible,
  topic,
  moduleIsConcluded,
  handleQuestionModalOpen,
  handleCreateBulkQuestionOpen,
}) => {
  const canViewCreate =
    (viaTopic && topic?.permission?.canViewCreateExerciseQuestion) ||
    (viaSession && isAccessible);

  if (!canViewCreate) return null;

  const getTooltipTitle = () => {
    if (viaTopic && !topic?.permission?.canCreateExerciseQuestion) {
      return `Questions for this Topic can be added by SPOCs of ${topic?.competency?.name} competency`;
    }
    if (moduleIsConcluded) {
      return "Module is completed";
    }
    return null;
  };

  const isDisabled = viaTopic
    ? !topic?.permission?.canCreateExerciseQuestion
    : moduleIsConcluded;

  return (
    <BootstrapTooltip title={getTooltipTitle()}>
      <Box>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          className="margin-left-2"
          onClick={handleQuestionModalOpen}
          disabled={isDisabled}
        >
          <Typography variant="outlineBtnLabel">Add question</Typography>
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          className="margin-left-1"
          onClick={handleCreateBulkQuestionOpen}
          disabled={isDisabled}
        >
          <Typography variant="outlineBtnLabel">Upload questions</Typography>
        </Button>
      </Box>
    </BootstrapTooltip>
  );
};

// Component for Header Section
const QuestionsHeader = ({
  viaModule,
  viaSession,
  sessionName,
  topicName,
  viaTopic,
  isAccessible,
  moduleIsConcluded,
  topic,
  handleAssociateQuestionsModalOpen,
  handleQuestionModalOpen,
  handleCreateBulkQuestionOpen,
}) => {
  return (
    <Grid item className="header-container" md={12}>
      <Typography variant="font_21_bold">
        {viaModule || viaSession ? sessionName : topicName}
      </Typography>
      <Box className="header-right-icons">
        <AssociateQuestionsButton
          viaSession={viaSession}
          isAccessible={isAccessible}
          moduleIsConcluded={moduleIsConcluded}
          handleAssociateQuestionsModalOpen={handleAssociateQuestionsModalOpen}
        />
        <QuestionActionButtons
          viaTopic={viaTopic}
          viaSession={viaSession}
          isAccessible={isAccessible}
          topic={topic}
          moduleIsConcluded={moduleIsConcluded}
          handleQuestionModalOpen={handleQuestionModalOpen}
          handleCreateBulkQuestionOpen={handleCreateBulkQuestionOpen}
        />
      </Box>
    </Grid>
  );
};

const Questions = () => {
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [getListId, setGetListId] = useState(0);
  const [offset, setOffset] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [associateQuestionsModalOpen, setAssociateQuestionsModalOpen] =
    useState(false);
  const [isEditQuestion, setIsEditQuestion] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [questionDetails, setQuestionDetails] = useState({
    exercise: [],
  });

  const dispatch = useDispatch();
  const question = useSelector((state) => state?.questionList);

  const questionList = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * 20;
    const lastPageIndex = firstPageIndex + 20;
    return question?.questionList?.data?.results?.slice(
      firstPageIndex,
      lastPageIndex
    );
  }, [currentPage, question]);

  const loading = question?.loading;

  const associateQuestionsList = question?.associateQuestionsList;

  const totalResult = question?.questionList?.data?.totalResults;
  const handlePageChange = (value) => {
    setOffset(value - 1);
    setCurrentPage(value);
  };

  useEffect(() => {
    setTotalPages(Math.ceil(totalResult / 20));
  }, [totalResult]);

  const moduleView = useSelector((state) => {
    return state?.bootcampModuleView;
  });
  const Details = useSelector((state) => {
    return state?.detailsTab;
  });
  const security = useSelector((state) => state?.security);
  const isSpoc = security?.loggedInUser?.permission?.spoc;

  const moduleData = moduleView?.moduleData?.data;
  const moduleName = moduleData?.name;
  const moduleBootcamps = moduleData?.bootcamps?.results;
  const sessionName = Details?.details?.data?.name;
  const sessionPermission = Details?.details?.data?.permission;
  const isAccessible =
    sessionPermission?.sessionPresenter || sessionPermission?.spoc;
  const moduleIsConcluded =
    Details?.details?.data?.module?.status === "COMPLETED";

  const sessionIsScheduled =
    checkDateTimeStatus(Details?.details?.data?.startDate) === "future";
  const associatedTopicId = Details?.details?.data?.topic?.id;
  const topic = question?.topicData?.data;
  const topicName = question?.topicData?.data?.name;

  const params = useParams();
  const { topicId, sessionId, moduleId, bootcampId } = params;
  const viaSession = !!params?.sessionId || false;
  const viaModule = !!params?.moduleId || false;
  const [bootcampName, setBootcampName] = useState(null);

  const viaTopic = !!params?.topicId && !viaSession;

  const type = viaModule || viaSession ? "SESSION" : "TOPIC";
  const id = viaModule || viaSession ? sessionId : topicId;

  // Helper function to check if session params are valid
  const isValidSessionParams = (bootcampId, sessionId, moduleId) => {
    return !isNaN(+bootcampId) && !isNaN(+sessionId) && !isNaN(+moduleId);
  };

  // Helper function to check if topic/session params are valid
  const isValidTopicSessionParams = (topicId, sessionId) => {
    if (topicId && sessionId) {
      return !isNaN(+topicId) && !isNaN(+sessionId);
    }
    if (topicId) {
      return !isNaN(+topicId);
    }
    return false;
  };

  // Helper function to fetch session data
  const fetchSessionData = async (
    bootcampId,
    moduleId,
    topicId,
    sessionId,
    viaModule
  ) => {
    const detailsApiValue = await dispatch(
      getDetails({ bootcampId, moduleId, topicId, sessionId })
    );
    let moduleApiValue = null;
    if (viaModule) {
      moduleApiValue = await dispatch(
        getBootcampModuleView({ bootcampId, moduleId })
      );
    }
    return { detailsApiValue, moduleApiValue };
  };

  // Helper function to fetch topic/session data
  const fetchTopicSessionData = async (
    topicId,
    sessionId,
    bootcampId,
    moduleId,
    viaSession
  ) => {
    let topicApiValue = null;
    let detailsApiValue = null;

    if (topicId && !viaSession) {
      topicApiValue = await dispatch(getTopicViaId({ id: +topicId }));
    }
    if (sessionId) {
      detailsApiValue = await dispatch(
        getDetails({ bootcampId, moduleId, topicId, sessionId })
      );
    }
    return { topicApiValue, detailsApiValue };
  };

  // Helper function to fetch question list
  const fetchQuestionList = async (
    id,
    type,
    offset,
    bootcampId,
    moduleId,
    sessionId,
    topicId
  ) => {
    return await dispatch(
      getQuestionList({
        id,
        type,
        offset,
        bootcampId,
        moduleId,
        sessionId,
        topicId,
      })
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      if (viaSession && isValidSessionParams(bootcampId, sessionId, moduleId)) {
        await fetchSessionData(
          bootcampId,
          moduleId,
          topicId,
          sessionId,
          viaModule
        );
      } else if (isValidTopicSessionParams(topicId, sessionId)) {
        await fetchTopicSessionData(
          topicId,
          sessionId,
          bootcampId,
          moduleId,
          viaSession
        );
      } else {
        navigate("/error", { replace: true });
        setIsLoading(false);
        return;
      }

      await fetchQuestionList(
        id,
        type,
        offset,
        bootcampId,
        moduleId,
        sessionId,
        topicId
      );
      setIsLoading(false);
    };

    if (security?.loggedInUser?.id) {
      fetchData();
    }
  }, [offset]);

  ///////////////////////  handle error ////////////////////////////////
  const initialError = {
    question: "",
  };
  const [questionApiError, setQuestionApiError] = useState(false);
  const [error, setError] = useState(initialError);

  const validateFields = (questionDetails) => {
    const { exercise } = questionDetails;
    const newErrors = {
      question:
        exercise?.length && exercise[0]?.trim() !== ""
          ? ""
          : "Question is required",
    };
    setError(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };
  //////////////////////////////////////////////////////////////////////////
  useEffect(() => {
    const matchedBootcamp = moduleBootcamps?.find(
      (item) => item.id === +bootcampId
    );
    if (matchedBootcamp) {
      setBootcampName(matchedBootcamp?.name);
    }
  }, [moduleBootcamps]);

  useEffect(() => {
    return () => {
      dispatch(clearQuestionList());
    };
  }, []);

  // Helper function to get breadcrumbs for viaModule
  const getViaModuleBreadcrumbs = (
    bootcampName,
    bootcampId,
    moduleName,
    moduleId,
    sessionId
  ) => {
    return [
      { label: "Home", url: "/" },
      {
        label: bootcampName ? bootcampName : "Bootcamps",
        url: "/",
        isBootcamp: true,
        bootcampId: bootcampId,
      },
      {
        label: moduleName ? moduleName : "Module",
        url: `/bootcamp/${bootcampId}/module/${moduleId}` || "/",
        isModule: true,
        moduleId: moduleId,
      },
      {
        label: "Sessions",
        url: `/bootcamp/${bootcampId}/module/${moduleId}/sessions` || "/",
        isSession: true,
        sessionId: sessionId,
      },
      { label: "Questions" },
    ];
  };

  // Helper function to get breadcrumbs for viaSession (not viaModule)
  const getViaSessionBreadcrumbs = (topicId, sessionId) => {
    return [
      {
        label: "Topics",
        url: "/topic",
        isTopic: true,
        topicId: topicId,
      },
      {
        label: "Sessions",
        url: `/topic/${topicId}/sessions`,
        isSession: true,
        sessionId: sessionId,
      },
      { label: "Questions" },
    ];
  };

  // Helper function to get breadcrumbs for viaTopic
  const getViaTopicBreadcrumbs = (topicId) => {
    return [
      {
        label: "Topics",
        url: "/topic",
        isTopic: true,
        topicId: topicId,
      },
      { label: "Questions" },
    ];
  };

  // Helper function to get breadcrumbs based on route type
  const getBreadcrumbs = () => {
    if (viaModule) {
      return getViaModuleBreadcrumbs(
        bootcampName,
        bootcampId,
        moduleName,
        moduleId,
        sessionId
      );
    }
    if (viaSession && !viaModule) {
      return getViaSessionBreadcrumbs(topicId, sessionId);
    }
    return getViaTopicBreadcrumbs(topicId);
  };

  const breadcrumbs = getBreadcrumbs();

  const handleDeleteModalOpen = (QuestionId) => {
    setGetListId(QuestionId);
    setDeleteModalOpen(true);
  };
  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
  };
  const handleDelete = async () => {
    const data = {
      questId: getListId,
      id,
      type,
      offset,
    };
    await dispatch(deleteQuestion(data));
    setDeleteModalOpen(false);
  };

  const handleQuestionModalOpen = () => {
    setQuestionApiError(false);
    setError(initialError);
    setIsEditQuestion(false);
    setQuestionModalOpen(true);
  };

  const handleQuestionModalClose = () => {
    setQuestionModalOpen(false);
    setGetListId(0);
    setQuestionApiError(false);
    setError(initialError);
    setQuestionDetails({
      exercise: [],
    });
  };

  const handleQuestionDetails = (exercise) => {
    if (questionApiError || error) {
      setQuestionApiError(false);
      setError(initialError);
    }
    setQuestionDetails((prevDetails) => ({
      ...prevDetails,
      exercise: [exercise],
    }));
  };

  const handleQuestionSubmit = async () => {
    if (validateFields(questionDetails)) {
      let apiValue = null;
      if (isEditQuestion) {
        apiValue = await dispatch(
          editQuestion({
            id,
            quesId: getListId,
            type,
            offset,
            exercise: questionDetails.exercise[0],
          })
        );
      } else {
        apiValue = await dispatch(
          addQuestion({
            id,
            type,
            offset,
            exercise: questionDetails.exercise,
          })
        );
      }
      if (apiValue?.error?.message === "Rejected") {
        setQuestionApiError(true);
      } else {
        await dispatch(getQuestionList({ id, type, offset }));
        handleQuestionModalClose();
      }
    }
  };

  const handleEditQuestionModalOpen = (questionId, questionText) => {
    setIsEditQuestion(true);
    setGetListId(questionId);
    setQuestionModalOpen(true);
    setQuestionDetails({
      exercise: [questionText],
      questionId,
    });
  };

  const handleAssociateQuestionsModalClose = () => {
    setAssociateQuestionsModalOpen(false);
  };

  const handleAssociateQuestionsDetails = (associatedId) => {
    const isSelected = selectedIds.includes(associatedId);
    if (isSelected) {
      setSelectedIds(
        selectedIds.filter((selectedId) => selectedId !== associatedId)
      );
    } else {
      setSelectedIds([...selectedIds, associatedId]);
    }
  };

  const selectAllChange = (event) => {
    if (event.currentTarget.checked) {
      setSelectedIds(
        associateQuestionsList?.data?.results?.map((question) => question.id)
      );
    } else {
      setSelectedIds([]);
    }
  };

  const handleAssociateQuestionSubmit = async (selectedQuestions) => {
    setIsLoading(true);
    await dispatch(
      associateQuestions({
        associateExerciseIds: selectedQuestions,
        sessionId: id,
        type,
        offset,
      })
    );
    setIsLoading(false);
    handleAssociateQuestionsModalClose();
    setSelectedIds([]);
  };

  const handleAssociateQuestionsModalOpen = async () => {
    setAssociateQuestionsModalOpen(true);

    await dispatch(
      getAssociateQuestions({
        id: associatedTopicId,
        type: Constants.TOPIC,
        offset,
      })
    );
  };

  /////////////////// Disassociate question modal /////////////////////////////
  const [openQuestionRemove, setOpenQuestionRemove] = useState(false);
  const [disassociateId, setDisassociateId] = useState(null);
  const handleOpenDisassociate = (value) => {
    setDisassociateId(value);
    setOpenQuestionRemove(true);
  };
  const handleCloseDisassociate = () => {
    setOpenQuestionRemove(false);
  };

  const handleDisassociateQuesiton = async () => {
    let apiValue = null;
    const data = {
      disassociateExerciseId: disassociateId,
      sessionId: Number(params?.sessionId),
    };
    apiValue = await dispatch(disassociateQuestion(data));
    if (apiValue?.error?.message === "Rejected") {
      setOpenQuestionRemove(false);
    } else {
      await dispatch(
        getQuestionList({
          id,
          type,
          offset,
          bootcampId,
          moduleId,
          sessionId,
          topicId,
        })
      );
      setOpenQuestionRemove(false);
    }
  };

  const [createBulkQuestionOpen, setCreateBulkQuestionOpen] = useState(false);
  const [bulkQuestionCreationError, setBulkQuestionCreationError] =
    useState(false);
  const [file, setFile] = useState(null);

  const handleCreateBulkQuestionChange = (file) => {
    setFile(file);
    setBulkQuestionCreationError(false);
  };

  const handleCreateBulkQuestionOpen = () => {
    setCreateBulkQuestionOpen(true);
  };

  const handleCreateBulkQuestionClose = () => {
    setCreateBulkQuestionOpen(false);
    setFile(null);
    setBulkQuestionCreationError(false);
  };

  const handleCreateBulkQuestionSubmit = async () => {
    const api = await dispatch(
      addBulkQuestions({
        json: {
          id,
          type,
        },
        files: [file],
      })
    );
    if (api?.payload?.errors?.length) {
      setBulkQuestionCreationError(api?.payload?.errors?.[0]);
    } else {
      handleCreateBulkQuestionClose();
    }
    if (api?.payload.data) {
      await dispatch(getQuestionList({ id, type, offset: 0 }));
    }
  };

  /////////////////////////////////////////////////////////////////////////////

  if ((loading && isLoading) || isLoading) return <Loader />;

  return (
    <Grid container className="main-container">
      <BreadCrumb breadcrumbs={breadcrumbs} />

      <QuestionsHeader
        viaModule={viaModule}
        viaSession={viaSession}
        sessionName={sessionName}
        topicName={topicName}
        viaTopic={viaTopic}
        isAccessible={isAccessible}
        moduleIsConcluded={moduleIsConcluded}
        topic={topic}
        handleAssociateQuestionsModalOpen={handleAssociateQuestionsModalOpen}
        handleQuestionModalOpen={handleQuestionModalOpen}
        handleCreateBulkQuestionOpen={handleCreateBulkQuestionOpen}
      />
      <Grid item md={12} className="body-container">
        <Grid container className="table-parent-container">
          <Paper
            elevation={0}
            className="flex-container-column question-paper-container"
          >
            {questionList && questionList.length ? (
              <QuestionTable
                questionList={questionList}
                totalPages={totalPages}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
                handleDeleteModalOpen={handleDeleteModalOpen}
                handleQuestionModalOpen={handleQuestionModalOpen}
                questionEditModal={(questionId, questionText) =>
                  handleEditQuestionModalOpen(questionId, questionText)
                }
                canViewActions={viaTopic || (viaSession && isAccessible)}
                moduleIsConcluded={moduleIsConcluded}
                isDisabled={viaTopic ? false : moduleIsConcluded}
                viaSession={viaSession}
                viaTopic={viaTopic}
                exerciseCount={totalResult}
                handleOpenDisassociate={handleOpenDisassociate}
              />
            ) : (
              <Grid container className="no-data-container">
                <NoDataAvailable
                  imgHeight={4}
                  imgWidth={4.5}
                  message="No Questions Available"
                />
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      <DeleteModal
        open={deleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleDelete={handleDelete}
      />

      <QuestionModal
        handleClose={handleQuestionModalClose}
        isEditQuestion={isEditQuestion}
        open={questionModalOpen}
        questionDetails={questionDetails}
        questionDetailsChange={handleQuestionDetails}
        handleQuestionSubmit={handleQuestionSubmit}
        questionApiError={questionApiError}
        error={error}
      />

      <QuestionUploadModal
        open={createBulkQuestionOpen}
        onClose={handleCreateBulkQuestionClose}
        handleChange={handleCreateBulkQuestionChange}
        data={file}
        handleCreateSubmit={handleCreateBulkQuestionSubmit}
        questionCreationError={bulkQuestionCreationError}
      />

      <AssociateQuestions
        handleClose={handleAssociateQuestionsModalClose}
        open={associateQuestionsModalOpen}
        associateQuestionsDetails={associateQuestionsList}
        associateQuestionsChange={(associatedId) =>
          handleAssociateQuestionsDetails(associatedId)
        }
        handleAssociateQuestionSubmit={(selectedQuestions) =>
          handleAssociateQuestionSubmit(selectedQuestions)
        }
        selectedIds={selectedIds}
        selectAllChange={(e) => selectAllChange(e)}
        questionsList={question?.questionList?.data?.results}
        setSelectedIds={setSelectedIds}
      />

      <DisassociateQuestionModal
        open={openQuestionRemove}
        handleClose={handleCloseDisassociate}
        handleRemove={handleDisassociateQuesiton}
      />
    </Grid>
  );
};

export default Questions;
